import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface NotificationPopupProps {
  notification: {
    id: string;
    title: string;
    message: string;
    type: string;
  } | null;
  onClose: () => void;
}

export function NotificationPopup({ notification, onClose }: NotificationPopupProps) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (notification) {
      setIsVisible(true);
      // Auto-dismiss após 8 segundos
      const timer = setTimeout(() => {
        handleClose();
      }, 8000);

      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
    }, 300); // Tempo para animação de saída
  };

  if (!notification) return null;

  const getTypeStyles = (type: string) => {
    switch (type) {
      case 'error':
        return 'border-destructive bg-destructive/5';
      case 'warning':
        return 'border-orange-500 bg-orange-500/5';
      case 'success':
        return 'border-green-500 bg-green-500/5';
      default:
        return 'border-primary bg-primary/5';
    }
  };

  return (
    <div 
      className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${
        isVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
    >
      <Card 
        className={`w-[400px] max-w-[90vw] transition-all duration-300 shadow-2xl ${
          isVisible ? 'scale-100 translate-y-0' : 'scale-95 translate-y-4'
        } ${getTypeStyles(notification.type)}`}
      >
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">{notification.title}</CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="h-6 w-6 p-0 hover:bg-muted"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground leading-relaxed">
            {notification.message}
          </p>
          <div className="flex justify-end mt-4 pt-2 border-t">
            <Button onClick={handleClose} size="sm">
              Entendi
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}